import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { DEFAULT_USER_PREFS, formatDateTimePref, normalizeUserPrefs } from "@strat/api";
import type { TablePrefs, UserPrefs } from "@strat/api";

import { dataService } from "../services/api";
import { currentDateDisplay, setDateDisplay } from "../utils/format";

export { currentDateDisplay };
import { usePreferences } from "./PreferencesProvider";

/**
 * A person's preferences (see `UserPrefs` in @strat/api), available to every
 * page.
 *
 * Where they live depends on who is looking. Signed in on the hub, they belong
 * to the account and follow it from device to device. In the desktop app they
 * belong to the machine. A visitor who isn't signed in keeps them in this
 * browser only. All three cases keep a copy in localStorage, so the first paint
 * already has them, and saves wait briefly so that a run of quick changes is
 * stored once.
 *
 * The theme is the one choice two stores hold. The header toggle writes it to
 * this device (`PreferencesProvider`, which index.html reads before first
 * paint), and this provider copies it to the account. An account that has a
 * theme applies it to each device it signs in on.
 */

const CACHE_KEY = "strat.userPrefs.v1";
const SAVE_DELAY_MS = 600;

type Updater = (prefs: UserPrefs) => UserPrefs;

interface UserPrefsContextValue {
  prefs: UserPrefs;
  update: (fn: Updater) => void;
  reset: () => void;
  /** Where a change is kept: `account` (the hub), `machine` (desktop) or `browser`. */
  scope: "account" | "machine" | "browser";
  /** The last save failed; the change is still applied here. */
  saveError: string | null;
}

const UserPrefsContext = createContext<UserPrefsContextValue | null>(null);

function readCache(): UserPrefs {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) return normalizeUserPrefs(JSON.parse(raw));
  } catch {
    // Blocked or corrupt storage: the defaults are a complete set of preferences.
  }
  return DEFAULT_USER_PREFS;
}

function writeCache(prefs: UserPrefs): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(prefs));
  } catch {
    // Not cached this time; the server copy (if any) still has it.
  }
}

export function UserPrefsProvider({
  account,
  scope = "account",
  children,
}: {
  /** Whose preferences: an account id, `"local"` for the desktop, or null for a visitor (browser only). */
  account: string | null;
  scope?: "account" | "machine";
  children: ReactNode;
}) {
  const [prefs, setPrefs] = useState<UserPrefs>(readCache);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const pending = useRef<{ timer: number; prefs: UserPrefs } | null>(null);
  const { prefs: device, update: updateDevice } = usePreferences();

  // Load the stored copy whenever the account changes.
  useEffect(() => {
    if (!account) {
      setLoadedFor(null);
      return;
    }
    let cancelled = false;
    dataService
      .getPreferences()
      .then((stored) => {
        if (cancelled) return;
        setPrefs(stored);
        writeCache(stored);
        setLoadedFor(account);
        if (stored.display.theme && stored.display.theme !== device.theme)
          updateDevice({ theme: stored.display.theme });
      })
      .catch(() => {
        // Offline or signed out between renders: the cached copy stands, and
        // nothing is saved over a copy that was never read.
        if (!cancelled) setLoadedFor(null);
      });
    return () => {
      cancelled = true;
    };
    // The device theme is read once per load, not a reason to load again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  const flush = useCallback(() => {
    const job = pending.current;
    if (!job) return;
    pending.current = null;
    window.clearTimeout(job.timer);
    dataService
      .savePreferences(job.prefs)
      .then(() => setSaveError(null))
      .catch((err: unknown) => setSaveError(err instanceof Error ? err.message : String(err)));
  }, []);

  const canSave = !!account && loadedFor === account;

  const update = useCallback(
    (fn: Updater) => {
      setPrefs((current) => {
        const next = normalizeUserPrefs(fn(current));
        writeCache(next);
        if (canSave) {
          if (pending.current) window.clearTimeout(pending.current.timer);
          pending.current = { prefs: next, timer: window.setTimeout(flush, SAVE_DELAY_MS) };
        }
        return next;
      });
    },
    [canSave, flush],
  );

  // A save still waiting when the page goes away is sent rather than dropped.
  useEffect(() => {
    window.addEventListener("pagehide", flush);
    return () => {
      window.removeEventListener("pagehide", flush);
      flush();
    };
  }, [flush]);

  // The header's theme toggle changes this device; copy a real change to the account.
  const lastDeviceTheme = useRef(device.theme);
  useEffect(() => {
    if (device.theme === lastDeviceTheme.current) return;
    lastDeviceTheme.current = device.theme;
    if (prefs.display.theme !== device.theme) {
      update((p) => ({ ...p, display: { ...p.display, theme: device.theme } }));
    }
  }, [device.theme, prefs.display.theme, update]);

  // Set during render, not in an effect, so the first paint already formats
  // dates the chosen way. Idempotent: it only records the current choice.
  setDateDisplay(prefs.display);

  const value = useMemo<UserPrefsContextValue>(
    () => ({
      prefs,
      update,
      reset: () => {
        update(() => DEFAULT_USER_PREFS);
      },
      scope: account ? scope : "browser",
      saveError,
    }),
    [prefs, update, account, scope, saveError],
  );

  return <UserPrefsContext.Provider value={value}>{children}</UserPrefsContext.Provider>;
}

/**
 * The preferences and a way to change them. Outside a provider (a component
 * rendered on its own) it still works, for that component's lifetime only.
 */
export function useUserPrefs(): UserPrefsContextValue {
  const ctx = useContext(UserPrefsContext);
  const [local, setLocal] = useState<UserPrefs>(DEFAULT_USER_PREFS);
  const localUpdate = useCallback((fn: Updater) => setLocal((p) => normalizeUserPrefs(fn(p))), []);
  return (
    ctx ?? {
      prefs: local,
      update: localUpdate,
      reset: () => setLocal(DEFAULT_USER_PREFS),
      scope: "browser",
      saveError: null,
    }
  );
}

/** One table's remembered columns, sort and filters, by its stable id. */
export function useTablePrefs(tableId: string | undefined) {
  const { prefs, update } = useUserPrefs();
  const table: TablePrefs = (tableId && prefs.tables[tableId]) || {};
  const set = useCallback(
    (patch: Partial<TablePrefs> | null) => {
      if (!tableId) return;
      update((p) => {
        const tables = { ...p.tables };
        if (patch === null) delete tables[tableId];
        else tables[tableId] = { ...tables[tableId], ...patch };
        return { ...p, tables };
      });
    },
    [tableId, update],
  );
  return { table, set };
}

/** Formats timestamps the way this person chose (Settings → Display). */
export function useDateFormat(): (ms: number, options?: { time?: boolean }) => string {
  const { prefs } = useUserPrefs();
  const { dateFormat, timeZone } = prefs.display;
  return useCallback(
    (ms: number, options?: { time?: boolean }) => formatDateTimePref(ms, { dateFormat, timeZone }, options),
    [dateFormat, timeZone],
  );
}
